import React from "react"
import { useTranslation } from "react-i18next"

import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

interface ProductIndicatorsProps {
  isLoading?: boolean
  hasPrice?: boolean
  hasConfigurationModel?: boolean
  hasExternalDimensioningModel?: boolean
}

interface IndicatorIconProps {
  isActive: boolean
  activeIcon: IconName
  inactiveIcon: IconName
  functionName: string
  titleId?: string
}

const IndicatorIcon = React.memo(
  ({
    isActive,
    activeIcon,
    inactiveIcon,
    functionName
  }: IndicatorIconProps) => {
    const { t } = useTranslation()
    const iconId = React.useId()
    const titleId = `${iconId}-title`
    const title = t(
      isActive
        ? "Portfolio.FunctionAvailable"
        : "Portfolio.FunctionUnavailable",
      { function: functionName }
    )

    return (
      <>
        <title id={titleId} className="visually-hidden">
          {title}
        </title>
        <Icon
          name={isActive ? activeIcon : inactiveIcon}
          size={IconSize.MEDIUM}
          className={isActive ? "active" : "inactive"}
          role="img"
          aria-label={title}
          aria-labelledby={titleId}
        />
      </>
    )
  }
)

export const PortfolioCardProductIndicators = React.memo(
  ({
    hasConfigurationModel,
    hasExternalDimensioningModel,
    hasPrice
  }: ProductIndicatorsProps) => {
    const { t } = useTranslation()

    return (
      <>
        <IndicatorIcon
          isActive={!!hasPrice}
          activeIcon={IconName.EURO}
          inactiveIcon={IconName.EURO_SLASH}
          functionName={t("Price.Price")}
        />
        <IndicatorIcon
          isActive={!!hasConfigurationModel}
          activeIcon={IconName.CONFIGURATION}
          inactiveIcon={IconName.CONFIGURATION_DISABLED}
          functionName={t("Product.Configuration.Configuration")}
        />
        <IndicatorIcon
          isActive={!!hasExternalDimensioningModel}
          activeIcon={IconName.DIMENSIONING}
          inactiveIcon={IconName.DIMENSIONING_DISABLED}
          functionName={t("Product.Configuration.Dimensioning")}
        />
      </>
    )
  }
)
